import { useMemo } from "react";
import { useNavigate, useParams } from "react-router";

import { URLS } from "config/urls";
import theme from "theme";
import { colors } from "theme/palette";
import { reverse } from "utils/urls";

import Tabs, { Tab } from "components/Tabs";

import { ECompanySettingsFeatureType } from "entities/Company/components/CompanySettings/constants";
import { useCheckFeature } from "entities/Company/components/CompanySettings/utils";
import { useCompanySettings } from "entities/Company/sdk";
import { ProformaSubNavItems } from "entities/Proforma/constants";

const tabs = [
  { label: "Sources & Uses", key: ProformaSubNavItems.SOURCE_AND_USES },
  {
    label: "Development Budget",
    key: ProformaSubNavItems.DEVELOPMENT_BUDGET,
  },
  { label: "Rental Income", key: ProformaSubNavItems.RENTAL_INCOME },
  { label: "Income & OPEX", key: ProformaSubNavItems.INCOME_AND_OPEX },
  { label: "P&L Projections", key: ProformaSubNavItems.PL_PROJECTIONS },
  { label: "Other Data", key: ProformaSubNavItems.OTHER_DATA },
];

const ProformaTabs = () => {
  const navigate = useNavigate();

  const params = useParams<{
    dealId: string;
    tab: string;
  }>();
  const dealId = Number(params.dealId);
  const currentTab = params.tab;
  const { data: companySettings } = useCompanySettings();
  const filteredTabs = useMemo(
    () =>
      tabs.filter(
        ({ key }) =>
          key !== ProformaSubNavItems.OTHER_DATA ||
          companySettings?.deal_tables_shown
      ),
    [companySettings]
  );

  const handleOtherDataTabClick = useCheckFeature({
    feature: ECompanySettingsFeatureType.DEAL_TABLES,
    callback: () =>
      navigate(
        reverse(URLS.DEAL_PROFORMA_TAB, {
          dealId,
          tab: ProformaSubNavItems.OTHER_DATA,
        })
      ),
  });

  const handleClick = ({ key }: { key: ProformaSubNavItems }) => {
    if (key === ProformaSubNavItems.OTHER_DATA) {
      handleOtherDataTabClick();
    } else {
      navigate(
        reverse(URLS.DEAL_PROFORMA_TAB, {
          dealId,
          tab: key,
        })
      );
    }
  };

  if (!currentTab) {
    return null;
  }

  return (
    <Tabs
      value={currentTab}
      sx={{
        borderBottom: `1px solid ${colors.blue100}`,
        minHeight: "40px",
      }}
      data-testid="proforma-tabs"
    >
      {filteredTabs.map(({ label, key }) =>
        key === ProformaSubNavItems.OTHER_DATA ? (
          <Tab
            key={key}
            label={label}
            value={key}
            onClick={() => handleClick({ key })}
            sx={{
              padding: theme.spacing(1, 2),
              color: colors.black,
              minHeight: "40px",
              "&.Mui-selected": {
                color: colors.black,
                backgroundColor: "#EEEEFF",
              },
            }}
            data-testid="proforma-tab"
          />
        ) : (
          <Tab
            key={key}
            label={label}
            value={key}
            onClick={() => handleClick({ key })}
            sx={{
              padding: theme.spacing(1, 2),
              color: colors.black,
              minHeight: "40px",
              "&.Mui-selected": {
                color: colors.black,
                backgroundColor: "#EEEEFF",
              },
            }}
            data-testid="proforma-tab"
          />
        )
      )}
    </Tabs>
  );
};

export default ProformaTabs;
